/**
 * GroupCart API client.
 *
 * Endpoints live in `online_ordering_service` under `/v1/online-ordering/group-carts/...` and
 * a few branch-scoped probes under `/v1/online-ordering/branches/{branchId}/tables/{tableId}`.
 *
 * Auth model:
 *   - Create / probe / join: API-key + (optionally) the customer's user JWT.
 *     We use the standard `apiFetch` flow which already injects the customer
 *     token and X-API-KEY.
 *   - All cart-scoped reads/writes (snapshot, line CRUD, fire, verify, leave,
 *     SSE): the participant token is the *only* auth — we explicitly skip the
 *     customer JWT and pass `Authorization: Bearer <participantToken>`.
 *
 * OCC: write endpoints take `If-Match: <version>` and return 412 with a
 * `{ currentVersion, cart }` body when the caller's version is stale. We
 * surface the parsed body so callers can re-base their state.
 */

import { ApiError, apiFetch, type ApiFetchInit } from "./http";

// --- Types --------------------------------------------------------------------

export type GroupCartStatus = "active" | "closed" | "abandoned";

export interface GroupCartParticipant {
  guestId: string;
  displayName: string;
  verified: boolean;
  customerId: string | null;
  colorIndex: number;
  joinedAt: string;
  lastSeenAt: string;
}

export interface GroupCartDraftLine {
  lineId: string;
  addedByGuestId: string;
  catalogItemId: string;
  variantSelections: unknown[];
  modifierSelections: unknown[];
  quantity: number;
  notes: string | null;
  addedAt: string;
}

export interface GroupCart {
  groupCartId: string;
  tenantId: string;
  branchId: string;
  tableId: string;
  floorId: string | null;
  status: GroupCartStatus;
  inviteCode: string;
  orderId: string | null;
  version: number;
  createdByGuestId: string;
  participants: GroupCartParticipant[];
  draftLines: GroupCartDraftLine[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrJoinResponse {
  groupCartId: string;
  inviteCode: string;
  version: number;
  participantToken: string;
  participant: GroupCartParticipant;
  cart: GroupCart;
  /** Server returns 201 on creation, 200 on join — we surface that as a flag. */
  createdNew: boolean;
}

export interface ActiveCartProbe {
  groupCartId: string;
  inviteCode: string;
  hostDisplayName: string | null;
  participantCount: number;
  version: number;
  status: GroupCartStatus;
}

export interface AddLineInput {
  catalogItemId: string;
  quantity: number;
  variantSelections?: unknown[];
  modifierSelections?: unknown[];
  notes?: string | null;
}

export interface AddLineResponse {
  line: GroupCartDraftLine;
  version: number;
}

export interface UpdateLinePatch {
  quantity?: number;
  variantSelections?: unknown[];
  modifierSelections?: unknown[];
  notes?: string | null;
}

export interface VersionResponse {
  version: number;
}

export interface FireResponse {
  orderId: string;
  firedLineIds: string[];
  version: number;
}

export interface VerifyResponse {
  participantToken: string;
  version: number;
}

export interface VersionConflictBody {
  currentVersion: number;
  cart: GroupCart;
}

/** Raised for 412 Precondition Failed; carries the server's resync payload. */
export class GroupCartVersionConflict extends Error {
  readonly currentVersion: number;
  readonly cart: GroupCart;

  constructor(body: VersionConflictBody) {
    super(`Group cart version conflict (server is at v${body.currentVersion})`);
    this.name = "GroupCartVersionConflict";
    this.currentVersion = body.currentVersion;
    this.cart = body.cart;
  }
}

// --- Internals ----------------------------------------------------------------

function withParticipant(token: string, init: ApiFetchInit = {}): ApiFetchInit {
  const headers = new Headers(init.headers ?? {});
  // The Tappya API Gateway tries to validate any `Authorization: Bearer ...`
  // against Firebase Auth and 401s before reaching the backend, so we use
  // the gateway-passthrough `X-Participant-Token` header instead. The
  // backend's `require_participant` checks this header first. We also skip
  // the customer JWT to avoid leaking the wrong identity to the route.
  headers.set("X-Participant-Token", `Bearer ${token}`);
  return { ...init, skipAuth: true, headers };
}

function withIfMatch(version: number, init: ApiFetchInit = {}): ApiFetchInit {
  const headers = new Headers(init.headers ?? {});
  headers.set("If-Match", String(version));
  return { ...init, headers };
}

async function withConflict<T>(p: Promise<T>): Promise<T> {
  try {
    return await p;
  } catch (err) {
    if (err instanceof ApiError && err.status === 412 && err.body) {
      throw new GroupCartVersionConflict(err.body as VersionConflictBody);
    }
    throw err;
  }
}

// --- Surface ------------------------------------------------------------------

export const groupCartApi = {
  /** Create-or-join the active cart for a (branch, table). 201 when newly
   *  created, 200 when this guest joined an existing one. */
  async createOrJoin(input: {
    branchId: string;
    tableId: string;
    floorId?: string | null;
    displayName: string;
    deviceFingerprint: string;
    seedLines?: AddLineInput[];
  }): Promise<CreateOrJoinResponse> {
    // The fetcher returns 200 OR 201 — we don't get the status code via
    // apiFetch, so we infer createdNew from the absence of pre-existing
    // participants other than ours. This is a reasonable approximation since
    // the consumer only uses createdNew for telemetry/copy.
    const body = await apiFetch<Omit<CreateOrJoinResponse, "createdNew">>(
      "/v1/online-ordering/group-carts",
      { method: "POST", body: input },
    );
    const createdNew = body.cart.participants.length <= 1;
    return { ...body, createdNew };
  },

  /** Public probe — given a (branch, table) does an active cart exist? */
  async probeActive(branchId: string, tableId: string): Promise<ActiveCartProbe | null> {
    try {
      return await apiFetch<ActiveCartProbe>(
        `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}/active-group-cart`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  joinByCode(
    code: string,
    input: { displayName: string; deviceFingerprint: string },
  ): Promise<CreateOrJoinResponse> {
    return apiFetch<CreateOrJoinResponse>(
      `/v1/online-ordering/group-carts/by-code/${encodeURIComponent(code)}/join`,
      { method: "POST", body: input },
    ).then((r) => ({ ...r, createdNew: false }));
  },

  joinById(
    groupCartId: string,
    input: { displayName: string; deviceFingerprint: string },
  ): Promise<CreateOrJoinResponse> {
    return apiFetch<CreateOrJoinResponse>(
      `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/join`,
      { method: "POST", body: input },
    ).then((r) => ({ ...r, createdNew: false }));
  },

  get(groupCartId: string, token: string): Promise<GroupCart> {
    return apiFetch<GroupCart>(
      `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}`,
      withParticipant(token),
    );
  },

  addLine(
    groupCartId: string,
    version: number,
    input: AddLineInput,
    token: string,
  ): Promise<AddLineResponse> {
    return withConflict(
      apiFetch<AddLineResponse>(
        `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/lines`,
        withIfMatch(version, { ...withParticipant(token), method: "POST", body: input }),
      ),
    );
  },

  updateLine(
    groupCartId: string,
    version: number,
    lineId: string,
    patch: UpdateLinePatch,
    token: string,
  ): Promise<VersionResponse> {
    return withConflict(
      apiFetch<VersionResponse>(
        `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/lines/${encodeURIComponent(lineId)}`,
        withIfMatch(version, { ...withParticipant(token), method: "PATCH", body: patch }),
      ),
    );
  },

  removeLine(
    groupCartId: string,
    version: number,
    lineId: string,
    token: string,
  ): Promise<VersionResponse> {
    return withConflict(
      apiFetch<VersionResponse>(
        `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/lines/${encodeURIComponent(lineId)}`,
        withIfMatch(version, { ...withParticipant(token), method: "DELETE" }),
      ),
    );
  },

  fire(
    groupCartId: string,
    version: number,
    token: string,
    posId?: string | null,
  ): Promise<FireResponse> {
    return withConflict(
      apiFetch<FireResponse>(
        `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/fire`,
        withIfMatch(version, {
          ...withParticipant(token),
          method: "POST",
          // Menu-link branch config can pin a posInstance; only the first
          // fire (order creation) uses it — appended rounds ignore it.
          ...(posId ? { body: { posId } } : {}),
        }),
      ),
    );
  },

  /** Verify the participant against the customer JWT — returns a *new*
   *  participant token whose `verified` claim is true. */
  verifyMe(
    groupCartId: string,
    customerId: string,
    token: string,
  ): Promise<VerifyResponse> {
    return apiFetch<VerifyResponse>(
      `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/participants/me/verify`,
      { ...withParticipant(token), method: "POST", body: { customerId } },
    );
  },

  leave(groupCartId: string, version: number, token: string): Promise<VersionResponse> {
    return withConflict(
      apiFetch<VersionResponse>(
        `/v1/online-ordering/group-carts/${encodeURIComponent(groupCartId)}/participants/me`,
        withIfMatch(version, { ...withParticipant(token), method: "DELETE" }),
      ),
    );
  },
};

export type GroupCartApi = typeof groupCartApi;
